/**
 * Validación local de comprobantes de pago (US-2.4, TG-53).
 *
 * Espejo de las reglas del backend: solo PDF (`application/pdf`), JPG
 * (`image/jpeg`) y PNG (`image/png`), con extensión coherente. El backend vuelve a
 * validar MIME + magic bytes + SHA-256 y aplica el tope de tamaño configurable
 * (`SETTLEMENT_MAX_RECEIPT_BYTES`, 10 MB por defecto); aquí se valida formato para
 * dar feedback inmediato y los errores de tamaño/duplicado se traducen desde la API.
 */
import type { CustomerReceiptMimeType } from '@goelectric/shared-types';

/** `accept` del selector de archivos (extensiones + MIME permitidos). */
export const CUSTOMER_RECEIPT_ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';

/** MIME admitidos por el contrato. */
export const CUSTOMER_RECEIPT_MIME_TYPES: readonly CustomerReceiptMimeType[] = [
  'application/pdf',
  'image/jpeg',
  'image/png',
];

const EXTENSION_BY_MIME: Record<CustomerReceiptMimeType, readonly string[]> = {
  'application/pdf': ['.pdf'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
};

export type CustomerReceiptValidation =
  | { valid: true; mimeType: CustomerReceiptMimeType }
  | { valid: false; error: string };

/** Extensión en minúsculas del nombre (con punto), o cadena vacía. */
function fileExtension(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return dot >= 0 ? filename.slice(dot).toLowerCase() : '';
}

/**
 * Valida un comprobante contra el contrato. Devuelve el MIME normalizado cuando es
 * aceptable o un mensaje en español cuando no lo es.
 */
export function validateCustomerReceipt(
  file: Pick<File, 'name' | 'type' | 'size'>,
): CustomerReceiptValidation {
  const mime = (file.type || '').toLowerCase();
  const extension = fileExtension(file.name);
  const allowed = CUSTOMER_RECEIPT_MIME_TYPES.includes(mime as CustomerReceiptMimeType);

  if (!allowed || !EXTENSION_BY_MIME[mime as CustomerReceiptMimeType]?.includes(extension)) {
    return {
      valid: false,
      error: 'Formato no permitido: solo se aceptan comprobantes PDF, JPG o PNG.',
    };
  }
  if (file.size <= 0) {
    return { valid: false, error: 'El archivo está vacío.' };
  }
  return { valid: true, mimeType: mime as CustomerReceiptMimeType };
}

/** Nombre corto del MIME para etiquetas de la tabla. */
export function receiptMimeLabel(mime: string): string {
  switch (mime) {
    case 'application/pdf':
      return 'PDF';
    case 'image/jpeg':
      return 'JPG';
    case 'image/png':
      return 'PNG';
    default:
      return mime;
  }
}
